import { FC } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Warning, Siren, X, Check, ArrowRight } from '@phosphor-icons/react';
import {
  recoveryEngineEndpoints,
  IRecoveryAlert,
} from '../../../endpoints/recoveryEngine';
import styles from './RecoveryAlertsBanner.module.scss';

/**
 * Banner alert Recovery Engine (intelligenza recupero) nel Session Builder.
 * Spec v1.1.1 sez 13 righe 1079-1099.
 *
 * Mostra alert attivi per il team: pattern back-to-back, monotonia, ACWR, MD-1,
 * ecc. Il coach può: [Ignora], [Applica suggerimento], [Modifica piano].
 */
export const RecoveryAlertsBanner: FC<{
  teamId?: string;
  affectedSessionId?: string;
}> = ({ teamId, affectedSessionId }) => {
  const queryClient = useQueryClient();

  const { data: alerts = [] } = useQuery({
    queryKey: ['recoveryAlerts', teamId],
    queryFn: () =>
      recoveryEngineEndpoints
        .listAlerts({ teamId, status: 'active', limit: 20 })
        .then((r) => r.data),
    enabled: !!teamId,
    refetchInterval: 5 * 60_000,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['recoveryAlerts', teamId] });

  const actionMut = useMutation({
    mutationFn: (vars: {
      id: string;
      action: 'accepted' | 'ignored' | 'modified_plan';
    }) =>
      recoveryEngineEndpoints
        .registerAction(vars.id, vars.action)
        .then((r) => r.data),
    onSuccess: invalidate,
  });

  const ackMut = useMutation({
    mutationFn: (id: string) =>
      recoveryEngineEndpoints.acknowledge(id).then((r) => r.data),
    onSuccess: invalidate,
  });

  const relevantAlerts = affectedSessionId
    ? alerts.filter((a) => a.affectedSessions.includes(affectedSessionId))
    : alerts;

  if (relevantAlerts.length === 0) return null;

  return (
    <div className={styles.container}>
      {relevantAlerts.map((alert) => (
        <AlertCard
          key={alert._id}
          alert={alert}
          onIgnore={() =>
            actionMut.mutate({ id: alert._id, action: 'ignored' })
          }
          onApply={() => {
            actionMut.mutate({ id: alert._id, action: 'accepted' });
            ackMut.mutate(alert._id);
          }}
          onModify={() =>
            actionMut.mutate({ id: alert._id, action: 'modified_plan' })
          }
        />
      ))}
    </div>
  );
};

const AlertCard: FC<{
  alert: IRecoveryAlert;
  onIgnore: () => void;
  onApply: () => void;
  onModify: () => void;
}> = ({ alert, onIgnore, onApply, onModify }) => {
  const isCritical = alert.severity === 'critical';
  const Icon = isCritical ? Siren : Warning;

  return (
    <div
      className={`${styles.alert} ${
        isCritical ? styles.alertCritical : styles.alertWarning
      }`}
    >
      <div className={styles.alertLeft}>
        <Icon size={18} weight="fill" />
      </div>
      <div className={styles.alertBody}>
        <div className={styles.alertTitle}>
          {isCritical ? 'Pattern critico' : 'Pattern a rischio'} ·{' '}
          {formatTrigger(alert.triggerType)}
        </div>
        <div className={styles.alertMsg}>{alert.message}</div>
        {alert.suggestion && (
          <div className={styles.alertSuggestion}>
            <span className={styles.alertSuggestionLabel}>Suggerimento:</span>
            {alert.suggestion}
          </div>
        )}
      </div>
      <div className={styles.alertActions}>
        <button
          type="button"
          className={`${styles.btn} ${styles.btnGhost}`}
          onClick={onIgnore}
          title="Ignora questo alert"
        >
          <X size={12} /> Ignora
        </button>
        <button
          type="button"
          className={`${styles.btn} ${styles.btnPrimary}`}
          onClick={onApply}
        >
          <Check size={12} /> Applica
        </button>
        <button
          type="button"
          className={`${styles.btn} ${styles.btnOutline}`}
          onClick={onModify}
        >
          <ArrowRight size={12} /> Modifica
        </button>
      </div>
    </div>
  );
};

function formatTrigger(t: string): string {
  const map: Record<string, string> = {
    cognitive_back_to_back: 'Cognitivo back-to-back',
    physical_back_to_back: 'Fisico back-to-back',
    high_intensity_density: 'Densità alta intensità',
    weekly_monotony: 'Monotonia settimanale',
    acwr_projection: 'ACWR proiettato',
    cognitive_dominant_md1: 'Cognitivo a MD-1',
    physical_dominant_md1: 'Fisico alta intensità a MD-1',
    no_unload_day: 'Nessun giorno di scarico',
  };
  return map[t] ?? t;
}
